/** @format */
import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { FudisSelectOption } from '@funidata/ngx-fudis';
import { TranslocoService } from '@ngneat/transloco';
import { CourseUnitResultItem, OtmId } from 'common-typescript/types';
import { BehaviorSubject, mergeAll, Observable, of, Subject, take, toArray } from 'rxjs';
import { filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { LocaleService } from 'sis-common';
import { AppErrorHandler } from '../../error-handler/app-error-handler';
import { CreditRangePipe } from '../../number/credit-range.pipe';
import { OrganisationNameService } from '../../organisation/organisation-name.service';
import { CourseUnitEntityService, CourseUnitSearchQueryParams } from '../../service/course-unit-entity.service';

export interface CourseUnitSearchGroup {
    homeUniversityCourseUnits: FudisSelectOption<CourseUnitResultItem>[];
    cooperationNetworkCourseUnits: FudisSelectOption<CourseUnitResultItem>[];
}

@Component({
    selector: 'sis-course-unit-search-select',
    templateUrl: './course-unit-search-select.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class CourseUnitSearchSelectComponent {
    @Input({ required: true }) currentUniversityOrgId: OtmId;
    @Input({ required: true }) crossStudyFeaturesEnabled: boolean;
    @Input({ required: true }) cooperationNetworkIds: OtmId[];
    @Input({ required: true }) curriculumPeriodIds: OtmId[];
    @Input() excludedCourseUnitIds: Set<OtmId> = new Set();
    @Input() helpText: string;

    @Output() updateSelected = new EventEmitter<CourseUnitResultItem>();

    uiLang: string;

    courseUnitSelectControl = new FormControl<null | FudisSelectOption<CourseUnitResultItem>>(null);

    protected searchTextUpdateSubject = new Subject<string | null>();
    protected searchResults = new BehaviorSubject<CourseUnitSearchGroup>({
        homeUniversityCourseUnits: [],
        cooperationNetworkCourseUnits: [],
    });

    constructor(
        private readonly appErrorHandler: AppErrorHandler,
        private readonly courseUnitEntityService: CourseUnitEntityService,
        private readonly creditRangePipe: CreditRangePipe,
        private readonly translocoService: TranslocoService,
        private readonly organisationNameService: OrganisationNameService,
        private readonly localeService: LocaleService,
    ) {
        this.searchTextUpdateSubject
            .pipe(
                tap(() =>
                    this.searchResults?.next({
                        homeUniversityCourseUnits: [],
                        cooperationNetworkCourseUnits: [],
                    }),
                ),
                filter(textQuery => textQuery?.trim() !== ''),
                switchMap(textQuery => this.searchCourseUnits(textQuery)),
                takeUntilDestroyed(),
            )
            .subscribe();

        this.uiLang = this.localeService.getCurrentLanguage();
    }

    searchCourseUnits(textQuery: string): Observable<FudisSelectOption<CourseUnitResultItem>[]> {
        const searchParameters = {
            fullTextQuery: textQuery,
            uiLang: this.uiLang,
            orgRootId: this.currentUniversityOrgId,
            curriculumPeriodId: this.curriculumPeriodIds,
            validity: 'ONGOING_AND_FUTURE',
            limit: 100,
            start: 0,
        };

        const cooperationNetworkParameters = this.crossStudyFeaturesEnabled
            ? {
                  cooperationNetworkId: this.cooperationNetworkIds,
                  cooperationNetworkSearchType: 'COURSE_UNITS_COMBINED_WITH_COOPERATION_NETWORKS',
                  validityInCooperationNetwork: 'ONGOING',
              }
            : {};

        const queryParams = Object.assign(searchParameters, cooperationNetworkParameters) as CourseUnitSearchQueryParams;

        return this.courseUnitEntityService.searchActive(queryParams).pipe(
            map(searchResult => searchResult.searchResults),
            mergeAll(),
            filter((courseUnitResultItem: CourseUnitResultItem) => !this.excludedCourseUnitIds.has(courseUnitResultItem.id)),
            map((courseUnitResultItem: CourseUnitResultItem) => this.courseUnitResultItemToFudisOption(courseUnitResultItem)),
            mergeMap((selectOption: FudisSelectOption<CourseUnitResultItem>) => {
                // Determine if related to current university
                const isRelatedToCurrentUniversity = selectOption.value.universityOrgIds.includes(this.currentUniversityOrgId);
                if (isRelatedToCurrentUniversity) {
                    return of(selectOption);
                }
                return this.organisationNameService.getOrgName(selectOption.value.universityOrgIds[0]).pipe(
                    take(1),
                    map(universityName => ({
                        ...selectOption,
                        label: selectOption.label.concat(` | ${universityName}`),
                    })),
                );
            }),
            toArray(),
            tap((selectOptions: FudisSelectOption<CourseUnitResultItem>[]) => {
                this.searchResults?.next(
                    this.crossStudyFeaturesEnabled
                        ? {
                              homeUniversityCourseUnits: selectOptions.filter(option =>
                                  option.value.universityOrgIds.includes(this.currentUniversityOrgId),
                              ),
                              cooperationNetworkCourseUnits: selectOptions.filter(
                                  option => !option.value.universityOrgIds.includes(this.currentUniversityOrgId),
                              ),
                          }
                        : {
                              homeUniversityCourseUnits: [...selectOptions],
                              cooperationNetworkCourseUnits: [],
                          },
                );
            }),
            this.appErrorHandler.defaultErrorHandler(),
        );
    }

    private courseUnitResultItemToFudisOption(courseUnitResultItem: CourseUnitResultItem): FudisSelectOption<CourseUnitResultItem> {
        return {
            value: courseUnitResultItem,
            label: `${courseUnitResultItem.name} (${this.creditRangePipe.transform(courseUnitResultItem.credits)})`,
            subLabel: `${courseUnitResultItem.code} | ${this.translocoService.translate('SIS_COMPONENTS.STUDY.COURSE_UNIT')}`,
        };
    }
}
